import { useEffect, useRef, useState } from 'react';

/**
 * Sequences log lines for the workspace terminal. Events arrive from polling in batches;
 * rather than dropping a batch on screen at once, new lines wait in a queue and are typed
 * one at a time. Typing speeds up with the number of waiting lines, so a large batch still
 * drains in seconds.
 */

export interface Typable {
  id: string;
  text: string;
  /** When it happened. Lines from before the view opened are history: whole, one per tick. */
  at?: string;
  /** A placeholder such as "Thinking": typed like any line, then replaced by the real line. */
  pending?: boolean;
  /** Shown whole: it was already read on an earlier visit. */
  remembered?: boolean;
  /** Text that updates as it streams in (e.g. live model thinking): rendered whole, never typed. */
  live?: boolean;
}

export interface TypingState {
  /** Keys of lines that are fully shown. */
  settled: ReadonlySet<string>;
  /** Key of the line being typed, or '' when idle. */
  activeKey: string;
  /** Characters of the active line already shown. */
  length: number;
}

export interface TypedRow<T> {
  entry: T;
  /** Characters of `entry.text` to show. */
  length: number;
  /** This is the line currently being typed. */
  typing: boolean;
}

/** One tick is one step of typing: 2 characters per 100 ms is about 20 characters a second. */
export const TICK_MS = 100;
export const MIN_CHARS_PER_TICK = 2;
/** Beyond this many waiting lines the older ones appear whole so the view never lags far behind. */
export const MAX_QUEUE = 12;

export const typingKey = (entry: Typable) => (entry.pending ? `${entry.id}#pending` : entry.id);

const isInstant = (entry: Typable) => Boolean(entry.remembered || entry.live);

// shortcut: compares server timestamps with the browser clock, so skew moves the boundary; use a
// first-load marker from the data source if lines start typing (or not) on the wrong side of it.
const isHistory = (entry: Typable, openedAt?: number) =>
  openedAt !== undefined && Boolean(entry.at) && Date.parse(entry.at ?? '') < openedAt;

const waiting = <T extends Typable>(stream: T[], settled: ReadonlySet<string>) =>
  stream.filter((entry) => !isInstant(entry) && !settled.has(typingKey(entry)));

export const initialTyping = (stream: Typable[]): TypingState => ({
  settled: new Set(stream.map(typingKey)),
  activeKey: '',
  length: 0,
});

/** Moves typing forward by one tick. Returns the same state when there is nothing to type. */
export function advanceTyping<T extends Typable>(
  state: TypingState,
  stream: T[],
  openedAt?: number,
): TypingState {
  let queue = waiting(stream, state.settled);
  if (queue.length === 0) return state;

  let settled = state.settled;
  if (queue.length > MAX_QUEUE) {
    const skipped = queue.slice(0, queue.length - MAX_QUEUE);
    settled = new Set([...settled, ...skipped.map(typingKey)]);
    queue = queue.slice(-MAX_QUEUE);
  }

  const head = queue[0];
  if (!head) return state;
  const key = typingKey(head);
  if (isHistory(head, openedAt))
    return { settled: new Set(settled).add(key), activeKey: '', length: 0 };
  const typed = key === state.activeKey ? state.length : 0;
  const length = typed + MIN_CHARS_PER_TICK * queue.length;

  if (length >= head.text.length) {
    return { settled: new Set(settled).add(key), activeKey: '', length: 0 };
  }
  return { settled, activeKey: key, length };
}

/** The lines to draw now: finished lines whole, the head of the queue partly, the rest not yet. */
export function typedRows<T extends Typable>(
  stream: T[],
  state: TypingState,
  openedAt?: number,
): TypedRow<T>[] {
  const rows: TypedRow<T>[] = [];
  let typingShown = false;
  for (const entry of stream) {
    const key = typingKey(entry);
    if (isInstant(entry) || state.settled.has(key)) {
      rows.push({ entry, length: entry.text.length, typing: false });
    } else if (!typingShown) {
      typingShown = true;
      // History is never typed: it waits its turn, then appears whole.
      if (!isHistory(entry, openedAt))
        rows.push({ entry, length: key === state.activeKey ? state.length : 0, typing: true });
    }
  }
  return rows;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/**
 * Lines already on screen when the view opens appear whole. Lines that load afterwards but
 * happened before it opened (a refresh, earlier answers) appear whole, one line per tick; only
 * lines that happen after it opened are typed. With reduced motion everything appears whole.
 */
export function useTypedStream<T extends Typable>(stream: T[]) {
  const reduced = prefersReducedMotion();
  const [state, setState] = useState(() => initialTyping(stream));
  const [openedAt] = useState(() => Date.now());
  const streamRef = useRef(stream);

  useEffect(() => {
    streamRef.current = stream;
  });

  const busy = !reduced && waiting(stream, state.settled).length > 0;
  useEffect(() => {
    if (!busy) return;
    const timer = setInterval(
      () => setState((current) => advanceTyping(current, streamRef.current, openedAt)),
      TICK_MS,
    );
    return () => clearInterval(timer);
  }, [busy, openedAt]);

  const rows: TypedRow<T>[] = reduced
    ? stream.map((entry) => ({ entry, length: entry.text.length, typing: false }))
    : typedRows(stream, state, openedAt);
  return { rows, progress: state.length };
}
